import * as cookie from "cookie";
import * as setCookieParser from "set-cookie-parser";

import { areCookiesEqual, createCookie, isBrowser } from "./utils";
import { NextPageContextEnhanced } from "../../types/page";

export const defaultOptions = {
  expires:new Date(
      new Date().getTime() +
      Number(process.env.NEXT_PUBLIC_DEFAULT_COOKIES_EXPIRED_IN_MINUTES) * 60000
  ),
  path: "/",
  ...(typeof window !== "undefined"
    ? location.hostname.includes("localhost")
      ? { domain: location.hostname }
      : {
          domain:
            "." +
            location.hostname.split(".").reverse()[1] +
            "." +
            location.hostname.split(".").reverse()[0],
        }
    : {}),
};

/**
 * Parses cookies.
 *
 * @param {NextPageContextEnhanced} context NextJS page or API context, express context, null or undefined.
 * @param options Options that we pass down to `cookie` library.
 */
export function parseCookies(
  context?: NextPageContextEnhanced,
  options: cookie.CookieParseOptions = {},
) {

  if (isBrowser()) {
    return cookie.parse(document.cookie, { ...defaultOptions, ...options });
  }

  return context?.cookies || {};
}

/**
 * Sets a cookie.
 *
 * @param context NextJS page or API context, express context, null or undefined.
 * @param name The name of your cookie.
 * @param value The value of your cookie.
 * @param options Options that we pass down to `cookie` library.
 */
export function setCookie(
  context: NextPageContextEnhanced,
  name: string,
  value: string,
  options: cookie.CookieSerializeOptions = defaultOptions
) {
  if (!value && value !== undefined) {
    return;
  } else if (value === undefined) {
    value = "";
  }

  if (context?.cookies) {
    /**
     * To prevent sending set-cookie header to the client
     */
    if (context.cookies[name] === value) return;

    context.cookies[name] = value;
  }
  // SSR
  if (
    context?.res?.getHeader &&
    context?.res?.setHeader &&
    context.requestUri
  ) {
    options.domain = `.${context.hostNoSubDomain}`;
    console.log("-> setting cookie trace:", {
      value,
      name,
      reqUri: context.requestUri,
      options
    });

    let cookies = context.res.getHeader("Set-Cookie") || [];

    if (typeof cookies === "string") cookies = [cookies];
    if (typeof cookies === "number") cookies = [];

    /**
     * Parse cookies but ignore values - we've already encoded
     * them in the previous call.
     */
    const parsedCookies = setCookieParser.parse(cookies, {
      decodeValues: false,
    });

    /**
     * We create the new cookie and make sure that none of
     * the existing cookies match it.
     */
    const newCookie = createCookie(name, value, options);

    const cookiesToSet: string[] = [];

    parsedCookies.forEach((parsedCookie: setCookieParser.Cookie) => {
      if (!areCookiesEqual(parsedCookie, newCookie)) {
        /**
         * We serialize the cookie back to the original format
         * if it isn't the same as the new one.
         */
        const serializedCookie = cookie.serialize(
          parsedCookie.name,
          parsedCookie.value,
          {
            // we prevent reencoding by default, but you might override it
            encode: (val: string) => val,
            ...(parsedCookie as cookie.CookieSerializeOptions),
          }
        );

        cookiesToSet.push(serializedCookie);
      }
    });

    cookiesToSet.push(cookie.serialize(name, value, options));
    // Update the header.
    context.res.setHeader("Set-Cookie", cookiesToSet);
  }

  // Browser
  if (isBrowser()) {
    options = { ...defaultOptions, ...options };
    if (options && options.httpOnly) {
      throw new Error("Can not set a httpOnly cookie in the browser.");
    }

    document.cookie = cookie.serialize(name, value, options);
  }

  return {};
}

/**
 * Destroys a cookie with a particular name.
 *
 * @param ctx NextJS page or API context, express context, null or undefined.
 * @param name Cookie name.
 * @param options Options that we pass down to `cookie` library.
 */
export function destroyCookie(
  ctx: NextPageContextEnhanced,
  name: string,
  options: cookie.CookieSerializeOptions = defaultOptions
) {
  if(ctx?.isRedirectDone){
    return;
  }
  if (ctx?.cookies) {
    delete ctx.cookies[name];
  }
  let cookieOptions: cookie.CookieSerializeOptions = {
    ...(options || {}),
    maxAge: -1,
    httpOnly: false,
  };
  if (isBrowser()) {
    cookieOptions = {
      ...defaultOptions,
      ...(options || {}),
      expires: new Date("2013"),
      maxAge: -1,
    };
  }
  /**
   * We forward the request destroy to setCookie function
   * as it is the same function with modified maxAge value.
   */
  return setCookie(ctx, name, undefined, cookieOptions);
}

/* Utility Exports */

export default {
  set: setCookie,
  get: parseCookies,
  destroy: destroyCookie,
};
